import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { theme } from '../styles/GlobalStyles';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, ${theme.colors.background} 0%, ${theme.colors.surface} 100%);
`;

const BackgroundGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 80%, rgba(37, 99, 235, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(124, 58, 237, 0.1) 0%, transparent 50%);
  pointer-events: none;
`;

const FloatingOrbs = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const Orb = styled(motion.div)<{ size: number; x: number; y: number; delay: number }>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary});
  border-radius: 50%;
  opacity: 0.1;
  left: ${props => props.x}%;
  top: ${props => props.y}%;
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  z-index: 1;
  max-width: 1200px;
  padding: 0 ${theme.spacing.md};
  width: 100%;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
`;

const DescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  width: 100%;
  max-width: 800px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${theme.spacing.lg};
  }
`;

const DescriptionText = styled(motion.div)`
  flex: 1;
  font-size: 1.2rem;
  color: ${theme.colors.textSecondary};
  line-height: 1.8;
  text-align: left;
  max-width: 500px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    text-align: center;
    max-width: none;
  }
`;

const HeadshotContainer = styled(motion.div)`
  position: relative;
  flex-shrink: 0;
`;

const Headshot = styled(motion.img)`
  width: 250px;
  height: 300px;
  border-radius: 12px;
  object-fit: contain;
  object-position: center;
  box-shadow: ${theme.shadows.lg};
  transition: all 0.3s ease;
  background: linear-gradient(135deg, ${theme.colors.background}, rgba(255, 255, 255, 0.05));

  &:hover {
    transform: scale(1.02);
    box-shadow: ${theme.shadows.xl};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 200px;
    height: 240px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 180px;
    height: 216px;
  }
`;

const Greeting = styled(motion.div)`
  font-size: 1.2rem;
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing.md};
  font-weight: 500;
`;

const Name = styled(motion.h1)`
  font-size: clamp(3rem, 8vw, 5rem);
  font-weight: 700;
  margin-bottom: ${theme.spacing.lg};
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary}, ${theme.colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Title = styled(motion.h2)`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xl};
  font-weight: 600;
`;



const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary});
  color: white;
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: ${theme.shadows.lg};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl};
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: ${theme.spacing.xl};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const ScrollArrow = styled(motion.div)`
  width: 2px;
  height: 30px;
  background: ${theme.colors.primary};
  border-radius: 1px;
`;

const Hero: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const scrollToAbout = () => {
    const aboutSection = document.querySelector('#about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroSection id="hero" ref={ref}>
      <BackgroundGradient />
      
      <FloatingOrbs>
        <Orb
          size={100}
          x={20}
          y={20}
          delay={0}
          animate={{
            y: [0, -20, 0],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            delay: 0,
          }}
        />
        <Orb
          size={150}
          x={80}
          y={80}
          delay={2}
          animate={{
            y: [0, 30, 0],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            delay: 2,
          }}
        />
        <Orb
          size={80}
          x={60}
          y={10}
          delay={4}
          animate={{
            y: [0, -15, 0],
            opacity: [0.1, 0.18, 0.1],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            delay: 4,
          }}
        />
      </FloatingOrbs>

      <HeroContent>
        <TextContent>
          <Greeting
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            👋 Hello, I'm
          </Greeting>

          <Name
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Simon Kohnstamm
          </Name>

          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Director of Sales & Marketing | MBA | AWS Solutions Architect
          </Title>

          <DescriptionContainer>
            <DescriptionText
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              MBA graduate and experienced technology leader specializing in life sciences and biotech. 
              I drive revenue growth, build strategic partnerships, and deliver data management solutions 
              that solve real scientific challenges.
            </DescriptionText>

            <HeadshotContainer
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              <Headshot
                src="/simon-headshot.jpg"
                alt="Simon Kohnstamm"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />
            </HeadshotContainer>
          </DescriptionContainer>

          <CTAButton
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1.0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToAbout}
          >
            Learn More About Me
          </CTAButton>
        </TextContent>
      </HeroContent>

      <ScrollIndicator
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.8, delay: 1.5 }}
      >
        <span>Scroll to explore</span>
        <ScrollArrow
          animate={{
            y: [0, 10, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
          }}
        />
      </ScrollIndicator>
    </HeroSection>
  );
};

export default Hero;
